import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import kitanegoLogoWordmark from "./assets/kitanego-logo-wordmark.png";
import type { InterfaceLanguage } from "./i18n";

const FAQ_GROUPS = [
  {
    title: "About Kitanego",
    questions: [
      ["What is Kitanego?", "Kitanego is a consumer demand platform. Members share the everyday products, brands, buying frequency and planned spending that matter to them. Kitanego combines and summarises these signals to identify strong demand and negotiate with suppliers for better pricing opportunities."],
      ["Is Kitanego an online shop?", "No. Kitanego currently gathers demand signals and does not automatically sell products, take payment or place orders on your behalf."],
      ["How does Kitanego help members get better prices?", "When enough members select the same product or brand, Kitanego can present the combined demand to suppliers. That stronger evidence may help suppliers offer a lower price, subject to their minimum quantities, availability and terms."],
    ],
  },
  {
    title: "Membership and registration",
    questions: [
      ["Who can join Kitanego?", "Anyone who meets the registration requirements and is located in an area currently supported by Kitanego may create an account."],
      ["Why do you ask for my district?", "Your district helps Kitanego understand where demand exists and whether a supplier can realistically serve that area. Precise home coordinates are not shared with suppliers."],
      ["Why do I need to verify that I am human?", "Basic verification helps reduce fake accounts, bots and duplicate demand so that aggregated insights remain useful and credible."],
      ["Does it cost anything to become a member?", "No membership payment is required to register and submit a wishlist unless Kitanego clearly introduces and discloses a separate paid service in the future."],
    ],
  },
  {
    title: "Wishlists and demand signals",
    questions: [
      ["What is a monthly wishlist?", "A monthly wishlist records the products and brands you are interested in, how often you buy them, your planned amount and your opinion about receiving a better price."],
      ["Is my wishlist a confirmed order?", "No. A wishlist is an expression of demand only. It is not a purchase, payment, reservation or contractual commitment."],
      ["Can I update my wishlist?", "Yes. You can create a new monthly wishlist or update your information when your needs change. Current demand summaries use the information available at the time they are prepared."],
      ["Do I have to choose a specific brand?", "No. You may select a preferred brand when available, indicate that you are flexible, or focus on the product category itself."],
      ["Why do you ask about buying frequency and planned amount?", "These details help estimate the strength, regularity and potential volume of demand. They allow Kitanego to approach suppliers with a more realistic summary."],
    ],
  },
  {
    title: "Pricing and supplier opportunities",
    questions: [
      ["Does Kitanego guarantee the cheapest price?", "No. Kitanego aims to negotiate competitive pricing, but prices depend on supplier terms, minimum quantities, delivery area, availability and market conditions. Any offer will be shown clearly before a member chooses to participate."],
      ["What is a minimum requirement?", "A supplier may require a minimum number of interested members, units or total value before offering a special price. The requirement will vary by product and supplier."],
      ["What happens when enough demand is reached?", "Kitanego may confirm the opportunity with the supplier and notify eligible or interested members about the product, price, conditions, timing and any next steps."],
      ["What if the minimum requirement is not reached?", "The wishlist remains a demand signal, but no supplier offer is guaranteed. Kitanego may continue monitoring demand or close the opportunity without requiring a purchase."],
      ["Are supplier offers always available?", "No. Offers may be limited by time, stock, location, quantity or other supplier conditions. Availability and final terms will be stated with each opportunity."],
    ],
  },
  {
    title: "Notifications, privacy and control",
    questions: [
      ["How will I know about a pricing update?", "If you have allowed notifications, Kitanego may contact you through the details linked to your account when a relevant product reaches the required demand or receives a supplier pricing update."],
      ["Will suppliers see my personal information?", "Kitanego is designed to share combined and summarised demand with suppliers. Personal contact details and individual profiles are not shared unless you are clearly informed and give the required permission."],
      ["How does Kitanego use my data?", "Your information is used to operate your account, understand demand, improve the service, communicate relevant updates and support supplier negotiations in line with the Privacy Policy."],
      ["Can I change my notification preferences?", "Yes. You can update your profile and communication preferences where available. Essential account or security messages may still be sent when necessary."],
      ["Can I delete my account or request help?", "Yes. Contact Kitanego through the support channel to request account assistance, correction or deletion, subject to applicable legal and record-retention requirements."],
    ],
  },
] as const;

export default function FaqDialog({ language, onClose }: { language: InterfaceLanguage; onClose: () => void }) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  const closeLabel = language === "en" ? "Close" : "Tutup";

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  return createPortal(
    <div className="about-us-dialog faq-dialog" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <article className="about-us-dialog__window faq-dialog__window" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="about-us-dialog__header">
          <div className="about-us-dialog__brand">
            <img src={kitanegoLogoWordmark} alt="Kitanego" />
            <h2 id={titleId}>Frequently asked questions</h2>
          </div>
          <button ref={closeButton} type="button" className="about-us-dialog__close" onClick={onClose} aria-label={closeLabel}><X /></button>
        </header>

        <div className="faq-dialog__content">
          <p className="faq-dialog__intro">Find clear answers about membership, monthly wishlists, supplier negotiations, pricing opportunities and your privacy.</p>
          {FAQ_GROUPS.map((group, groupIndex) => (
            <section className="faq-dialog__group" key={group.title} aria-labelledby={`faq-group-${groupIndex}`}>
              <h3 id={`faq-group-${groupIndex}`}>{group.title}</h3>
              <div className="faq-dialog__questions">
                {group.questions.map(([question, answer], questionIndex) => (
                  <details key={question} open={groupIndex === 0 && questionIndex === 0}>
                    <summary>{question}<ChevronDown aria-hidden="true" /></summary>
                    <p>{answer}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </div>
      </article>
    </div>,
    document.body,
  );
}
